import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, KeyRound, ShieldCheck, X, Loader2, Users, UserCheck, UserX, Pencil,
  MoreHorizontal, Search, ArrowUpDown, ArrowUp, ArrowDown, Crown,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../lib/authStore';
import { TeamStore } from '../lib/teamStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users`;

// Title taxonomy mirrors the backend enum in auth.py
const LO_TITLES = ['Founding Broker', 'Executive Broker', 'Senior Broker', 'Broker'];
const TITLE_RANK = { 'Administrator': 0, 'Founding Broker': 1, 'Executive Broker': 2, 'Senior Broker': 3, 'Broker': 4 };

// avatarTone → bg colour + foreground. Drives both list avatars and the
// picker chips in the create/edit dialogs.
const TONE_COLORS = {
  ink:     { bg: '#0F1626', fg: '#FFFFFF' },
  gold:    { bg: '#D4A24A', fg: '#1B1306' },
  indigo:  { bg: '#3B3FB7', fg: '#FFFFFF' },
  rose:    { bg: '#C04A6E', fg: '#FFFFFF' },
  emerald: { bg: '#1B8A5A', fg: '#FFFFFF' },
  sky:     { bg: '#0E7AB3', fg: '#FFFFFF' },
  amber:   { bg: '#D08A1A', fg: '#FFFFFF' },
  neutral: { bg: '#3A3A3A', fg: '#FFFFFF' },
};
const AVATAR_TONES = Object.keys(TONE_COLORS);

// Relative-time formatter — "4 hours ago", "Yesterday", "Never"
function relTime(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 172800) return 'Yesterday';
  if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// Title chip — visible color encoding for rank. Founding Broker is gold,
// Executive is ink, Senior is sky, Broker is neutral. Administrator is gold inverse.
function TitleChip({ title, role }) {
  if (role === 'admin' || title === 'Administrator') {
    return (
      <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10.5px] uppercase tracking-[0.14em] font-bold bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/40">
        <Crown className="h-3 w-3" /> Administrator
      </span>
    );
  }
  const cls = {
    'Founding Broker': 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40',
    'Executive Broker': 'bg-neutral-900 text-white border-neutral-900',
    'Senior Broker':   'bg-sky-50 text-sky-700 border-sky-200',
    'Broker':          'bg-neutral-100 text-neutral-700 border-neutral-200',
  }[title] || 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return (
    <span className={cn('inline-flex items-center h-6 px-2 rounded-md text-[10.5px] uppercase tracking-[0.14em] font-bold border', cls)}>
      {title}
    </span>
  );
}

function Avatar({ user, size = 36 }) {
  const tone = TONE_COLORS[user?.avatarTone] || TONE_COLORS.neutral;
  return (
    <div
      className="rounded-full flex items-center justify-center font-bold shrink-0 border hairline"
      style={{ height: size, width: size, background: tone.bg, color: tone.fg, fontSize: size * 0.36 }}
    >
      {user?.initials || user?.name?.slice(0, 2).toUpperCase() || '?'}
    </div>
  );
}

export default function AdminTeam() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');   // all | admin | lo
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState({ col: 'rank', dir: 'asc' });

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API);
      setUsers(data);
      TeamStore.refresh();
    } catch (e) {
      toast.error('Failed to load team');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const toggleActive = async (u) => {
    try {
      const next = !u.is_active;
      await axios.patch(`${API}/${u.id}`, { is_active: next });
      toast.success(`${u.name} ${next ? 'reactivated' : 'deactivated'}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const filtered = useMemo(() => {
    let list = users;
    if (!showInactive) list = list.filter((u) => u.is_active);
    if (roleFilter !== 'all') list = list.filter((u) => u.role === roleFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((u) =>
        u.name.toLowerCase().includes(q)
        || u.email.toLowerCase().includes(q)
        || (u.title || '').toLowerCase().includes(q),
      );
    }
    const sorter = (a, b) => {
      const dir = sort.dir === 'asc' ? 1 : -1;
      if (sort.col === 'name') return a.name.localeCompare(b.name) * dir;
      if (sort.col === 'email') return a.email.localeCompare(b.email) * dir;
      if (sort.col === 'last_login') {
        const av = a.last_login_at ? new Date(a.last_login_at).getTime() : 0;
        const bv = b.last_login_at ? new Date(b.last_login_at).getTime() : 0;
        return (av - bv) * dir;
      }
      // rank: admin first, then by title rank, then by name
      const ar = TITLE_RANK[a.title] ?? 99;
      const br = TITLE_RANK[b.title] ?? 99;
      if (ar !== br) return (ar - br) * dir;
      return a.name.localeCompare(b.name);
    };
    return [...list].sort(sorter);
  }, [users, query, roleFilter, showInactive, sort]);

  const stats = useMemo(() => {
    const all = users;
    return {
      lo: all.filter((u) => u.role === 'lo' && u.is_active).length,
      admin: all.filter((u) => u.role === 'admin' && u.is_active).length,
      active: all.filter((u) => u.is_active).length,
      inactive: all.filter((u) => !u.is_active).length,
    };
  }, [users]);

  const setSortCol = (col) => {
    setSort((p) => p.col === col
      ? { col, dir: p.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' });
  };

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1280px] mx-auto space-y-6" data-testid="admin-team-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-400 font-bold flex items-center gap-2">
            <ShieldCheck className="h-3 w-3 text-[hsl(var(--kf-gold-deep))]" /> Admin · Team
          </div>
          <h2 className="text-[30px] font-bold tracking-tight text-neutral-900 mt-1 leading-none">Loan officers</h2>
          <p className="text-[13px] text-neutral-500 mt-3 max-w-2xl">
            Manage the brokers who can log into Kinfolk CRM. Accounts use the company email
            <span className="font-semibold text-[hsl(var(--kf-gold-deep))]"> name@kinfolkhomeloans.com</span>
            and a password you set here. Deactivating preserves all of their lead history.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          data-testid="admin-team-create-btn"
          className="h-10 px-4 rounded-md btn-ink text-[12.5px] font-bold inline-flex items-center gap-1.5"
        >
          <Plus className="h-4 w-4" /> Add broker
        </button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Brokers"        value={stats.lo}       icon={Users} />
        <Stat label="Administrators" value={stats.admin}    icon={ShieldCheck} accent="gold" />
        <Stat label="Active"         value={stats.active}   icon={UserCheck} accent="emerald" />
        <Stat label="Inactive"       value={stats.inactive} icon={UserX} dim />
      </div>

      {/* Table card */}
      <div className="rounded-2xl border hairline bg-white" data-testid="admin-team-table">
        {/* Toolbar */}
        <div className="px-5 py-3.5 border-b hairline flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[16px] font-bold text-neutral-900 tracking-tight">All team members</div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email or title…"
                data-testid="admin-team-search"
                className="h-8 pl-7 pr-3 rounded-md border hairline bg-white text-[12px] text-neutral-900 placeholder:text-neutral-400 placeholder:font-normal focus:outline-none focus:border-neutral-900 w-[260px]"
              />
            </div>
            {/* Role filter */}
            <Segmented value={roleFilter} onChange={setRoleFilter} options={[
              { v: 'all',   label: 'All' },
              { v: 'admin', label: 'Admins' },
              { v: 'lo',    label: 'Brokers' },
            ]} />
            {/* Inactive toggle */}
            <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-neutral-600 cursor-pointer select-none">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                data-testid="admin-team-show-inactive" className="h-3.5 w-3.5 accent-neutral-900" />
              Show inactive
            </label>
          </div>
        </div>

        {loading ? (
          <div className="px-5 py-16 text-center text-neutral-400 text-[12px] uppercase tracking-wider font-bold flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-16 text-center" data-testid="admin-team-empty">
            <Users className="h-7 w-7 text-neutral-300 mx-auto" strokeWidth={1.5} />
            <div className="mt-3 text-[13px] font-semibold text-neutral-700">No team members match</div>
            <div className="text-[11.5px] text-neutral-400 mt-1">Try clearing the search or toggling filters.</div>
          </div>
        ) : (
          <table className="w-full text-[12.5px]">
            <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-400 font-bold border-b hairline">
              <tr>
                <Th width="32%" col="name" sort={sort} setSort={setSortCol}>Person</Th>
                <Th width="28%" col="email" sort={sort} setSort={setSortCol}>Email</Th>
                <Th width="16%" col="rank" sort={sort} setSort={setSortCol}>Title</Th>
                <Th width="14%" col="last_login" sort={sort} setSort={setSortCol}>Last login</Th>
                <Th width="10%" right>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <tr
                    key={u.id}
                    className={cn(
                      'hover:bg-neutral-50/60 transition-colors',
                      // Deactivated rows get a subtle grey tint + a muted name
                      // colour, but we deliberately do NOT apply `opacity-60`
                      // to the whole row — that was making the "Reactivate"
                      // option in the actions menu look disabled/greyed out
                      // when admins opened it, even though the click handler
                      // was fully wired.
                      !u.is_active && 'bg-neutral-50/60',
                      u.role === 'admin' && 'bg-[hsl(var(--kf-gold-soft))]/30',
                    )}
                    data-testid={`admin-team-row-${u.id}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar user={u} />
                        <div className="min-w-0">
                          <div className={cn(
                            'font-bold truncate flex items-center gap-1.5',
                            u.is_active ? 'text-neutral-900' : 'text-neutral-500',
                          )}>
                            {u.name}
                            {isMe && (
                              <span className="text-[8.5px] uppercase tracking-wider font-bold bg-neutral-900 text-white rounded px-1 py-px leading-none" data-testid="admin-team-you-badge">You</span>
                            )}
                            {!u.is_active && (
                              <span className="text-[8.5px] uppercase tracking-wider font-bold bg-neutral-200 text-neutral-600 rounded px-1.5 py-px leading-none" data-testid={`admin-team-inactive-badge-${u.id}`}>Inactive</span>
                            )}
                          </div>
                          <div className="text-[10.5px] text-neutral-500">
                            {u.role === 'admin' ? 'Administrator' : 'Loan Officer'} · {u.is_active ? 'Active' : 'Deactivated'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-[12px] text-neutral-700 truncate">{u.email}</td>
                    <td className="px-3 py-3.5">
                      <TitleChip title={u.title} role={u.role} />
                    </td>
                    <td className="px-3 py-3.5 text-neutral-600">
                      {relTime(u.last_login_at)}
                    </td>
                    <td className="px-3 py-3.5 text-right">
                      <RowMenu
                        user={u}
                        isMe={isMe}
                        onEdit={() => setEditFor(u)}
                        onReset={() => setResetFor(u)}
                        onToggle={() => toggleActive(u)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && <CreateUserDialog onClose={() => setCreateOpen(false)} onCreated={refresh} />}
      {resetFor && <ResetPasswordDialog user={resetFor} onClose={() => setResetFor(null)} />}
      {editFor && <EditUserDialog user={editFor} onClose={() => setEditFor(null)} onSaved={refresh} />}
    </div>
  );
}

// ---- Subcomponents ------------------------------------------------------

function Th({ children, col, sort, setSort, right, width }) {
  const sortable = !!col && !!setSort;
  const active = sort?.col === col;
  return (
    <th
      onClick={sortable ? () => setSort(col) : undefined}
      style={{ width }}
      className={cn(
        'px-3 py-2.5 select-none',
        right && 'text-right',
        sortable && 'cursor-pointer hover:text-neutral-700',
      )}
    >
      {sortable
        ? (
          <span className={cn('inline-flex items-center gap-1', right && 'justify-end')}>
            {children}
            {active
              ? (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
              : <ArrowUpDown className="h-3 w-3 opacity-40" />}
          </span>
        ) : children
      }
    </th>
  );
}

function Stat({ label, value, icon: Icon, accent, dim }) {
  const tone = accent === 'gold'
    ? 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))]'
    : accent === 'emerald'
    ? 'bg-emerald-50 text-emerald-600'
    : 'bg-neutral-100 text-neutral-700';
  return (
    <div className={cn('rounded-2xl border hairline bg-white px-4 py-4 flex items-center gap-3', dim && 'opacity-70')}>
      <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center', tone)}>
        <Icon className="h-5 w-5" strokeWidth={2.2} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-bold">{label}</div>
        <div className="text-[26px] font-bold tracking-tight text-neutral-900 leading-none mt-1.5 tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-md border hairline bg-white p-0.5" data-testid="admin-team-role-filter">
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={cn(
            'h-7 px-2.5 rounded text-[11px] font-semibold transition',
            value === o.v ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:text-neutral-900',
          )}
        >{o.label}</button>
      ))}
    </div>
  );
}

function RowMenu({ user, isMe, onEdit, onReset, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`admin-team-actions-${user.id}`}
        className="h-7 w-7 rounded-md border hairline bg-white hover:border-neutral-400 inline-flex items-center justify-center text-neutral-500 hover:text-neutral-900 transition"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-48 rounded-md border hairline bg-white shadow-[0_18px_36px_-12px_rgba(0,0,0,0.18)] overflow-hidden">
          <button
            onClick={() => { setOpen(false); onEdit(); }}
            data-testid={`admin-team-edit-${user.id}`}
            className="w-full text-left px-3 py-2 text-[12px] font-semibold text-neutral-700 hover:bg-neutral-50 inline-flex items-center gap-2"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit details
          </button>
          <button
            onClick={() => { setOpen(false); onReset(); }}
            data-testid={`admin-team-reset-${user.id}`}
            className="w-full text-left px-3 py-2 text-[12px] font-semibold text-neutral-700 hover:bg-neutral-50 inline-flex items-center gap-2"
          >
            <KeyRound className="h-3.5 w-3.5" /> Reset password
          </button>
          {!isMe && (
            <button
              onClick={() => { setOpen(false); onToggle(); }}
              data-testid={`admin-team-toggle-${user.id}`}
              className={cn(
                'w-full text-left px-3 py-2 text-[12px] font-semibold inline-flex items-center gap-2 border-t hairline',
                user.is_active ? 'text-red-600 hover:bg-red-50' : 'text-emerald-700 hover:bg-emerald-50',
              )}
            >
              {user.is_active
                ? (<><UserX className="h-3.5 w-3.5" /> Deactivate</>)
                : (<><UserCheck className="h-3.5 w-3.5" /> Reactivate</>)}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---- Dialogs ------------------------------------------------------------

function CreateUserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: '', name: '', password: '', arive_email: '',
    role: 'lo', title: 'Broker', avatarTone: 'indigo',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => {
    const next = { ...p, [k]: v };
    // Keep title coherent with role.
    if (k === 'role') next.title = v === 'admin' ? 'Administrator' : 'Broker';
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.email || !form.name || !form.password) { toast.error('Email, name, and password are required'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await axios.post(API, {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        role: form.role,
        title: form.title,
        avatarTone: form.avatarTone,
        // Empty string → backend treats as "no override, use CRM email".
        arive_email: form.arive_email.trim().toLowerCase() || null,
      });
      toast.success(`${form.name} added`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create user');
    } finally { setSaving(false); }
  };

  return (
    <Modal onClose={onClose} testId="admin-team-create-dialog" title="Add broker">
      <form onSubmit={submit} className="space-y-4">
        <FormField label="Full name">
          <input data-testid="create-user-name" value={form.name} onChange={(e) => set('name', e.target.value)} className="kf-input" placeholder="Andrew Vega" autoFocus />
        </FormField>
        <FormField label="Email">
          <input data-testid="create-user-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="kf-input" placeholder="andrew@kinfolkhomeloans.com" />
        </FormField>
        <FormField label="ARIVE email" hint="The email this broker uses to log into ARIVE. Leave blank if it matches the CRM email above. Used as the originator when their leads are pushed to ARIVE.">
          <input
            data-testid="create-user-arive-email"
            type="email"
            value={form.arive_email}
            onChange={(e) => set('arive_email', e.target.value)}
            className="kf-input"
            placeholder="Same as CRM email by default"
          />
        </FormField>
        <FormField label="Temporary password" hint="Share this with them securely; they can change it after first login.">
          <input data-testid="create-user-password" value={form.password} onChange={(e) => set('password', e.target.value)} className="kf-input" placeholder="Min 8 characters" />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Role">
            <select data-testid="create-user-role" value={form.role} onChange={(e) => set('role', e.target.value)} className="kf-input">
              <option value="lo">Broker / Loan Officer</option>
              <option value="admin">Administrator</option>
            </select>
          </FormField>
          <FormField label="Title">
            <select data-testid="create-user-title" value={form.title} onChange={(e) => set('title', e.target.value)} className="kf-input" disabled={form.role === 'admin'}>
              {form.role === 'admin'
                ? <option value="Administrator">Administrator</option>
                : LO_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </FormField>
        </div>
        <FormField label="Avatar color">
          <AvatarPicker value={form.avatarTone} onChange={(v) => set('avatarTone', v)} />
        </FormField>
        <DialogActions onClose={onClose} saving={saving} testId="create-user-submit" label="Add broker" />
      </form>
    </Modal>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    arive_email: user.arive_email || '',
    title: user.title || (user.role === 'admin' ? 'Administrator' : 'Broker'),
    avatarTone: user.avatarTone || 'neutral',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/${user.id}`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        arive_email: form.arive_email.trim().toLowerCase(),
        title: form.title,
        avatarTone: form.avatarTone,
      });
      toast.success(`${form.name} updated`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} testId="admin-team-edit-dialog" title="Edit details">
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-center gap-3 -mt-2">
          <Avatar user={{ ...user, ...form }} size={44} />
          <div>
            <div className="font-bold text-neutral-900">{form.name || user.name}</div>
            <div className="text-[11px] text-neutral-500">{form.email || user.email}</div>
          </div>
        </div>
        <FormField label="Full name">
          <input data-testid="edit-user-name" value={form.name} onChange={(e) => set('name', e.target.value)} className="kf-input" autoFocus />
        </FormField>
        <FormField label="Email" hint="Used for CRM login. Changing this changes how this person signs in.">
          <input data-testid="edit-user-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="kf-input" />
        </FormField>
        <FormField label="ARIVE email" hint="The email this broker uses to log into ARIVE. Leave blank if it matches the CRM email above. Determines the originator when their leads push to ARIVE.">
          <input
            data-testid="edit-user-arive-email"
            type="email"
            value={form.arive_email}
            onChange={(e) => set('arive_email', e.target.value)}
            className="kf-input"
            placeholder="Same as CRM email by default"
          />
        </FormField>
        <FormField label="Title">
          <select data-testid="edit-user-title" value={form.title} onChange={(e) => set('title', e.target.value)} className="kf-input" disabled={user.role === 'admin'}>
            {user.role === 'admin'
              ? <option value="Administrator">Administrator</option>
              : LO_TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </FormField>
        <FormField label="Avatar color">
          <AvatarPicker value={form.avatarTone} onChange={(v) => set('avatarTone', v)} />
        </FormField>
        <DialogActions onClose={onClose} saving={saving} testId="edit-user-submit" label="Save changes" />
      </form>
    </Modal>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/${user.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset password');
    } finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} testId="admin-team-reset-dialog" title="Reset password">
      <form onSubmit={submit} className="space-y-4">
        <p className="text-[13px] text-neutral-600 -mt-2">Set a new password for <span className="font-bold text-neutral-900">{user.name}</span>. Share securely.</p>
        <FormField label="New password">
          <input data-testid="reset-password-input" value={pw} onChange={(e) => setPw(e.target.value)} className="kf-input" placeholder="Min 8 characters" autoFocus />
        </FormField>
        <DialogActions onClose={onClose} saving={saving} testId="reset-password-submit" label="Set new password" />
      </form>
    </Modal>
  );
}

function AvatarPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {AVATAR_TONES.map((t) => {
        const tone = TONE_COLORS[t];
        const active = value === t;
        return (
          <button
            key={t}
            type="button"
            onClick={() => onChange(t)}
            data-testid={`avatar-tone-${t}`}
            title={t}
            className={cn(
              'h-7 w-7 rounded-full transition border-2',
              active ? 'border-neutral-900 ring-2 ring-neutral-900/15' : 'border-transparent hover:scale-105',
            )}
            style={{ background: tone.bg }}
          />
        );
      })}
    </div>
  );
}

function Modal({ children, onClose, testId, title }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border hairline bg-white shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[18px] font-bold tracking-tight text-neutral-900">{title}</div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-900"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-neutral-400 mt-1.5">{hint}</div>}
    </label>
  );
}

function DialogActions({ onClose, saving, testId, label }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} className="h-9 px-3 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button>
      <button type="submit" disabled={saving} data-testid={testId} className="h-9 px-4 rounded-md btn-ink text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
        {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</> : label}
      </button>
    </div>
  );
}
